import { useState } from 'react';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { DataRow, DataTable, TableCell, TableHead } from '../ui/DataTable.js';
import { formatRelativeTime } from '../lib/format.js';
import { useAuth } from '../auth/useAuth.js';
import { InviteDialog } from './InviteDialog.js';
import { useUsers, type UserAccount } from './queries.js';

/** Email, role, status, joined, last sign-in. */
const COLUMNS = ['34%', '12%', '16%', '19%', '19%'] as const;

/** The date an account was created, written out rather than "3 months ago". */
const JOINED = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

/**
 * How each account reads.
 *
 * `invited` borrows the paused treatment rather than the active one: the
 * account exists and cannot be signed in to, which is what paused means
 * everywhere else in this console. An expired invitation is `deleted` — a
 * resting state that needs an action, not an error.
 */
function statusOf(user: UserAccount): { status: 'active' | 'paused' | 'deleted'; label: string } {
  if (user.status === 'active') return { status: 'active', label: 'Active' };
  if (user.status === 'invited') return { status: 'paused', label: 'Invited' };
  return { status: 'deleted', label: 'Invite expired' };
}

export function UsersScreen() {
  const { user } = useAuth();
  const users = useUsers();
  const [inviting, setInviting] = useState(false);
  const canEdit = user?.role === 'admin';

  const rows = users.data?.users ?? [];

  return (
    <section aria-labelledby="users-heading" className="flex flex-col gap-(--space-5)">
      <header className="flex flex-wrap items-end justify-between gap-(--space-4)">
        <div>
          <p className="text-[length:var(--text-micro)] font-medium uppercase tracking-[0.18em] text-ink-subtle">
            Access
          </p>
          <h1 id="users-heading" className="mt-(--space-1) text-[length:var(--text-display)]">
            Users
          </h1>
          <p className="mt-(--space-2) max-w-[64ch] text-[length:var(--text-small)] font-medium text-ink-muted">
            An account is created by invitation only. The person you invite chooses their own
            password through a one-time link, so nobody here ever holds somebody else&rsquo;s
            credentials.
          </p>
        </div>
        {canEdit && (
          <Button variant="primary" onClick={() => setInviting(true)}>
            Invite user
          </Button>
        )}
      </header>

      {users.isError && (
        <Callout tone="danger">
          {users.error instanceof Error ? users.error.message : 'Could not load the users'}
        </Callout>
      )}

      {users.isLoading && (
        <p role="status" className="text-ink-subtle">
          Loading users…
        </p>
      )}

      {rows.length > 0 && (
        <DataTable
          columns={COLUMNS}
          caption="Console accounts"
          head={
            <>
              <TableHead scope="col">Email</TableHead>
              <TableHead scope="col">Role</TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col">Joined</TableHead>
              <TableHead scope="col">Last sign-in</TableHead>
            </>
          }
        >
          {rows.map((account) => {
            const badge = statusOf(account);
            return (
              <DataRow key={account.id}>
                <TableCell className="py-(--space-3) font-bold text-ink">{account.email}</TableCell>
                <TableCell className="py-(--space-3) font-medium text-ink-muted">
                  {account.role === 'admin' ? 'Administrator' : 'Read-only'}
                </TableCell>
                <TableCell className="py-(--space-3)">
                  <StatusBadge status={badge.status} label={badge.label} />
                </TableCell>
                <TableCell className="py-(--space-3) tabular-nums text-ink-muted">
                  {JOINED.format(new Date(account.createdAt))}
                </TableCell>
                <TableCell className="py-(--space-3) tabular-nums text-ink-muted">
                  {formatRelativeTime(account.lastLoginAt)}
                </TableCell>
              </DataRow>
            );
          })}
        </DataTable>
      )}

      <InviteDialog open={inviting} onClose={() => setInviting(false)} />
    </section>
  );
}
